import { userAtom } from '@/atoms/user.atom';
import { batchStatusMap, formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import { ANALYSIS_RUN_FILES_KEY } from '@/lib/analyses/runFilesKey';
import { analysisIdForJobType } from '@/lib/analyses/runs';
import { analytics } from '@/lib/analytics';
import { isInternalUser } from '@/lib/email';
import { useAlert } from '@/providers/AlertProvider';
import { deleteBatchMutation, getBatch, resetBatchMutation } from '@/queries/attachment.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { FlaskConical, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import AttachmentList from './AttachmentList';
import ByproductList from './ByproductList';
import Header from './Header';
import JobList from './JobList';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';

const formatBytesShort = (bytes?: number | null): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

interface Props {
  batchId: string | null;
  project: Project;
  href: string;
  onClose: () => void;
}

export default function BatchDetailsDrawer({ batchId, project, href, onClose }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  return (
    <Sheet open={batchId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        ref={drawerRef}
        data-testid='batch-details'
        side='right'
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          drawerRef.current?.focus({ preventScroll: true });
        }}
        className='w-full gap-0 p-0 outline-none sm:max-w-xl'
      >
        {batchId && <BatchDetails key={batchId} batchId={batchId} project={project} href={href} onClose={onClose} />}
      </SheetContent>
    </Sheet>
  );
}

function BatchDetails({ batchId, project, href, onClose }: Props & { batchId: string }) {
  const { t } = useTranslation('project');
  const { t: tc } = useTranslation('components');
  const user = useAtomValue(userAtom);
  const isActegonUser = isInternalUser(user?.email);
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();

  const deleteBatch = useMutation({
    mutationFn: () => execute(deleteBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Deleted');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      // A deleted run / report batch takes its files off the Analyses page.
      queryClient.invalidateQueries({ queryKey: [ANALYSIS_RUN_FILES_KEY] });
      toast.success(t('batch.batchDeleted'));
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedDeleteBatch'));
    },
  });

  const resetBatch = useMutation({
    mutationFn: () => execute(resetBatchMutation, { id: batchId! }),
    onSuccess: () => {
      analytics.capture('Batch Reset');
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('batch.batchReset'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('batch.failedResetBatch'));
    },
  });

  const handleDelete = () => {
    openAlert({
      title: t('batch.deleteBatchTitle'),
      content: t('batch.deleteBatchDescription'),
      actions: [{ label: t('batch.delete'), variant: 'destructive', onClick: () => deleteBatch.mutate() }],
    });
  };

  const handleReset = () => {
    openAlert({
      title: t('batch.resetBatchTitle'),
      content: t('batch.resetBatchDescription'),
      actions: [{ label: t('batch.reset'), variant: 'destructive', onClick: () => resetBatch.mutate() }],
    });
  };

  const {
    data: batchData,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => execute(getBatch, { id: batchId! }),
    refetchInterval: (query) =>
      ['PROCESSING', 'UPLOADING'].includes(query.state.data?.batch?.status ?? '') ? 1000 : false,
  });

  const batch = batchData?.batch;
  // The Data Center list behind the drawer stays mounted and does not poll, so
  // a status change seen by this poll is passed on to it.
  const status = batch?.status;
  const [seenStatus, setSeenStatus] = useState(status);
  useEffect(() => {
    if (status === seenStatus) return;
    setSeenStatus(status);
    if (seenStatus && status) queryClient.invalidateQueries({ queryKey: ['batches'] });
  }, [status, seenStatus, queryClient]);
  const statusKeys = {
    UPLOADING: 'batchList.statusUploading',
    WAITING: 'batchList.statusWaiting',
    PROCESSING: 'batchList.statusProcessing',
    PROCESSED: 'batchList.statusProcessed',
    FAILED: 'batchList.statusFailed',
  } as const;
  if (!batch)
    return (
      <>
        <SheetHeader className='border-b border-slate-200/70 pr-10 dark:border-stone-800'>
          <SheetTitle>{t('batch.detailsTitle')}</SheetTitle>
          <SheetDescription>
            {isPending ? t('edit.loading') : isError ? t('upload.error') : t('batch.notFound')}
          </SheetDescription>
        </SheetHeader>
        {isPending && (
          <div className='space-y-4 p-4'>
            <Skeleton className='h-24 w-full' />
            <Skeleton className='h-40 w-full' />
          </div>
        )}
      </>
    );
  // An analysis run's batch links to its details on the Analyses page: the
  // root job (no parent) of a type the hub lists.
  const analysisRunJob =
    batch.kind === 'ANALYSIS'
      ? batch.jobs?.find((j) => !j.parentItemId && analysisIdForJobType(j.type) !== null)
      : undefined;
  return (
    <>
      <SheetHeader className='shrink-0 border-b border-slate-200/70 pr-10 dark:border-stone-800'>
        <SheetTitle className='break-words'>
          {batch.label || t('batch.batchKey', { key: batch.key.toUpperCase() })}
        </SheetTitle>
        <SheetDescription className='flex items-center gap-2 [&_svg]:size-4'>
          {batchStatusMap[batch.status].icon}
          {tc(statusKeys[batch.status])}
          <span className='ml-auto'>
            {t('batch.byproducts.totalSize', { size: formatBytesShort(batch.totalBytes) })}
          </span>
        </SheetDescription>
      </SheetHeader>
      <div className='min-h-0 flex-1 overflow-y-auto p-4'>
        <div className='border rounded-md border-slate-200/70 mt-0 bg-white dark:bg-stone-900 dark:border-stone-800 px-4 py-2.5 items-start gap-x-4 gap-y-3 grid grid-cols-2'>
          <div className='flex min-w-0 items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.status')}
            </span>
            <span className='min-w-0 break-words text-sm font-medium text-slate-900 dark:text-white text-left flex items-center gap-1 [&>svg]:size-4'>
              {batchStatusMap[batch?.status].icon}
              {tc(statusKeys[batch.status])}
            </span>
          </div>
          <div className='flex min-w-0 items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>{t('batch.label')}</span>
            <span className='min-w-0 break-words text-sm font-medium text-slate-900 dark:text-white text-left'>
              {t('batch.batchKey', { key: batch?.key?.toUpperCase() })}
            </span>
          </div>
          <div className='flex min-w-0 items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedBy')}
            </span>
            <span className='min-w-0 break-words text-sm font-medium text-slate-900 dark:text-white text-left flex items-center gap-1.5'>
              <img
                src={
                  batch?.createdBy?.avatar?.url ||
                  getPlaceholderAvatar(batch?.createdBy?.email || batch?.createdBy?.id || '')
                }
                alt={batch?.createdBy?.name || t('batch.unknown')}
                className='size-4 shrink-0 rounded-full'
              />
              {batch?.createdBy?.name || t('batch.unknown')}
            </span>
          </div>
          <div className='flex min-w-0 items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>
              {t('batch.uploadedAt')}
            </span>
            <span className='min-w-0 break-words text-sm font-medium text-slate-900 dark:text-white text-left'>
              {formatDateTime(batch?.createdAt ?? new Date())}
            </span>
          </div>
        </div>
        {batch.kind === 'ANALYSIS' && (
          <div className='mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-stone-400'>
            <span className='rounded-full border border-violet-200/70 bg-violet-50 px-2 py-0.5 font-medium text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-300'>
              {t('batchList.kindAnalysis', { ns: 'components' })}
            </span>
            <span>{t('batch.byproducts.analysisHint')}</span>
          </div>
        )}
        {batch.kind === 'UPLOAD' && batch.status === 'WAITING' && (
          <section className='mt-5 rounded-lg border border-slate-200/70 bg-slate-50/60 p-4 dark:border-stone-800 dark:bg-stone-900/50'>
            <Header
              batchId={batchId}
              status={batch.status}
              srid={batch.srid || project.srid}
              attachments={batch.attachments ?? []}
            />
          </section>
        )}
        {batch.kind === 'ANALYSIS' ? (
          <ByproductList byproducts={batch.byproducts ?? []} />
        ) : (
          <AttachmentList attachments={batch?.attachments ?? []} batchStatus={batch?.status} />
        )}
        <JobList jobs={batch?.jobs ?? []} />
      </div>
      <SheetFooter className='shrink-0 flex-row flex-wrap justify-end border-t border-slate-200/70 dark:border-stone-800'>
        {analysisRunJob && (
          <Button asChild variant='outline'>
            <Link data-testid='batch-view-in-analyses' to={`${href}/datacenter/analyses?run=${analysisRunJob.id}`}>
              <FlaskConical className='size-4 mr-2' />
              {t('batch.viewInAnalyses')}
            </Link>
          </Button>
        )}
        {isActegonUser && (
          <Button variant='outline' onClick={handleReset} disabled={resetBatch.isPending}>
            <RotateCcw className='size-4 mr-2' />
            {t('batch.resetBatch')}
          </Button>
        )}
        <Button variant='destructive' onClick={handleDelete} disabled={deleteBatch.isPending}>
          <Trash2 className='size-4 mr-2' />
          {t('batch.deleteBatch')}
        </Button>
      </SheetFooter>
    </>
  );
}
